import { ContentState } from 'draft-js'
import { Map, Set } from 'immutable'
import { curry } from 'ramda'

export interface ICustomColor {
  color: string
  caption: string
  className: string
}

export const defaultCustomColors: ICustomColor[] = [
  { color: '#ff3b30', caption: 'Red', className: 'hl_red' },
  { color: '#ff9500', caption: 'Orange', className: 'hl_orange' },
  { color: '#ffcc00', caption: 'Yellow', className: 'hl_yellow' },
  { color: '#4cd964', caption: 'Green', className: 'hl_green' },
  { color: '#5ac8fa', caption: 'Teal Blue', className: 'hl_tblue' },
  { color: '#007aff', caption: 'Blue', className: 'hl_blue' },
  { color: '#5856d6', caption: 'Purple', className: 'hl_purple' },
  { color: '#ff2d55', caption: 'Pink', className: 'hl_pink' },
]

export const COLOR_STYLE_PREFIX = 'COLOR-'
export const DEFAULT_COLOR = '#ffcc00'
export const DEFAULT_COLOR_NAME = 'Custom Color'

export const makeCustomColorName = (colorElement: ICustomColor): string =>
  `${COLOR_STYLE_PREFIX}${colorElement.className}`
export const makeCustomColorStyle = (
  colorElement: ICustomColor
): { borderBottom: string } => ({
  borderBottom: `4px solid ${colorElement.color}`,
})

function mergeCustomStyleMapFn(
  colors: ICustomColor[],
  styleMap: Map<string, string>
): Map<string, string> {
  if (!colors) {
    return styleMap
  }
  const mapWithoutColorStyles = styleMap
    .filter((_, key) => !!key && key.indexOf(COLOR_STYLE_PREFIX) === -1)
    .toMap()
  const colorMap = colors.reduce((result, colorElement) => {
    return {
      ...result,
      [makeCustomColorName(colorElement)]: Map(
        makeCustomColorStyle(colorElement)
      ),
    }
  }, {})
  return mapWithoutColorStyles.merge(Map(colorMap))
}

export const mergeCustomStyleMap = curry(mergeCustomStyleMapFn)

// Find highlight styles in content
export const getStoryColorClassesFromContentState = (
  contentState: ContentState
): Set<string> => {
  const blocks = contentState.getBlocksAsArray()
  let foundClasses = Set<string>()

  for (const block of blocks) {
    if (block.getType() === 'atomic') {
      continue
    }
    block.findStyleRanges(
      (value) => {
        foundClasses = foundClasses.union(
          value
            .getStyle()
            .filter(
              (styleClass) =>
                !!styleClass && styleClass.indexOf(COLOR_STYLE_PREFIX) === 0
            )
            .map((styleClass) =>
              (styleClass as string).split(COLOR_STYLE_PREFIX).join('')
            )
        )
        return false
      },
      () => 0
    )
  }
  return foundClasses
}

export const getColorClassesWithDefaults = (
  contentState: ContentState,
  colors: ICustomColor[]
): ICustomColor[] => {
  const storyClasses = getStoryColorClassesFromContentState(contentState)
  const existingClasses = Set(colors.map((color) => color.className))
  const combinedSetOfClasses = existingClasses.union(storyClasses)

  return combinedSetOfClasses
    .map((className) => {
      const existingColor = colors.find(
        (color) => color.className === className
      )
      if (existingColor) {
        return existingColor
      }
      const defaultColor: ICustomColor = {
        caption: DEFAULT_COLOR_NAME,
        className: className || '',
        color: DEFAULT_COLOR,
      }
      return defaultColor
    })
    .toArray()
}
